import type { DokumentRegel } from './types';

/**
 * Regeln: Stichwort in Bezeichnung oder Gewerk → Dokumente.
 *
 * ─── Wie gesucht wird ────────────────────────────────────────────────────
 * Stichworte stehen in `normalisiere`-Form und treffen als Teilstring, auch
 * INNERHALB eines Worts („fassadengeruest“ trifft „geruest“). Das ist fuer
 * deutsche Komposita gewollt, erzeugt aber Fehlgriffe („kran“ in
 * „Krankenhaus“, „ramm“ in „Programm“). Die werden gezielt ueber `ausser`
 * oder ueber praezisere Staemme verhindert; jeder Fehlgriff, der hier
 * abgefangen wird, hat einen Test in `vorschlag.test.ts`.
 *
 * Bewusst NICHT verwendet: nacktes „leitung“ (Bauleitung), „werk“ (Gewerk,
 * Mauerwerk), „bahn“ (Fahrbahn, Bitumenbahn), „gas“ (Gasse), „anker“
 * (Geruestanker), „lager“ (Auflager), „fluss“ (Abfluss), „strahlung“
 * (UV-Strahlung), „schwimmend“ (schwimmender Estrich), „befahren“ (Decke
 * befahren), „aufbruch“ (Bodenplatte), „am Wasser“ (Wasserhahn).
 *
 * Bedingungen (`bedingung`) sind nur Editorhinweise. Loest eine Regel ohne
 * Bedingung dasselbe Dokument aus, gilt es unbedingt.
 *
 * ─── Was keine Regel bekommt ─────────────────────────────────────────────
 * Art-C-Dokumente (Betriebsanleitung, Pruefnachweise, Gefaehrdungs-
 * beurteilung …) stehen im Grundsatz-Satz. Daher loesen Baugeraete,
 * Pruefungen von Arbeitsmitteln, Laerm und Hitze nichts aus. Entsorgung und
 * Abfall sind Umweltrecht und kein Arbeitsschutz. Die Reihenfolge der
 * Vorschlaege bestimmt der Katalog, nicht diese Liste.
 */

// ─── Wiederverwendete Stammlisten fuer die Kombiregeln Geraet × Lage ──────

/** Geraete, deren Last oder Abgas die Lage zum Problem macht. */
const GERAETE = [
    'bagger', 'lader', 'raupe', 'kran', 'betonpumpe', 'bohrgeraet', 'ramme', 'hubarbeitsbuehne',
    'lkw', 'silo', 'materiallager', 'container', 'dumper', 'stapler', 'fahrmischer', 'baumaschine',
];
/** Fehlgriffe der Geraetestaemme: Krankenhaus, Dachkranz, Programm/Kilogramm, Siloxan. */
const GERAETE_AUSSER = ['kranken', 'kranz', 'gramm', 'siloxan'];

/** Nicht nacktes „schwimmend“ — sonst traefe „schwimmender Estrich“. */
const LAGE_SCHWIMMEND = [
    'ponton', 'schute', 'barkasse', 'schwimmende geraet', 'schwimmendes geraet', 'schwimmenden geraet',
    'schwimmkoerper', 'schwimmkran', 'schwimmbagger', 'schwimmplattform', 'arbeitsschiff',
];
const LAGE_DECKE = [
    'decke', 'tiefgarage', 'bruecke', 'parkdeck', 'auf dem dach', 'dachflaeche', 'auf dem bauwerk',
];
/** Arbeiten AN oder UNTER der Decke (Hubarbeitsbuehne fuer Deckenmontage) belasten sie nicht. */
const LAGE_DECKE_AUSSER = [
    'deckenarbeit', 'deckenmontage', 'an der decke', 'unter der decke', 'an decken', 'unter decken',
    'abgehaengte decke', 'deckenbekleid', 'deckenkante', 'deckenaussenkante', 'deckeninnenkante', 'unterdecke',
];
const LAGE_BAUGRUBE = ['baugrube', 'boeschung', 'graben', 'verbau', 'grubenrand'];
const LAGE_UNTERGRUND = [
    'weicher untergrund', 'weichem untergrund', 'weichen untergrund', 'weicher boden', 'weichem boden',
    'aufgefuellt', 'auffuellung', 'unbekannter untergrund', 'unbekanntem untergrund',
    'nicht tragfaehig', 'wenig tragfaehig', 'gering tragfaehig', 'unbefestigt',
];
const LAGE_VERKEHRSRAUM = ['oeffentlich', 'strasse', 'gehweg', 'fahrbahn', 'verkehrsraum'];
const LAGE_INNENRAUM = [
    'halle', 'innenraum', 'innenraeum', 'innenbereich', 'in der tiefgarage', 'im gebaeude',
    'geschlossenen raeum', 'geschlossene raeum', 'geschlossenem raum', 'tunnel', 'unter tage',
];

/** Merkmale alter Mineralwolle (TRGS 521) — trennt Ausbau von neuer Daemmung. */
const KMF = ['mineralwolle', 'mineralfaser', 'kmf', 'glaswolle', 'steinwolle'];
const KMF_ALT = ['ausbau', 'rueckbau', 'sanier', 'abbruch', 'entfern', 'demontage', 'alte', 'alter', 'bestand'];

const KRAN_AUSSER = ['kranken', 'kranz'];

export const REGELN: DokumentRegel[] = [
    // ═══ Vorbereitung und Bestand ═══════════════════════════════════════════
    {
        id: 'betrieb-werk',
        stichworte: [
            'betriebsanlage', 'betriebsgelaende', 'werksgelaende', 'werkgelaende', 'werksvorschrift',
            'laufender betrieb', 'laufendem betrieb', 'laufenden betrieb', 'industrieanlage', 'produktionsanlage',
        ],
        dokumente: ['werksvorschriften', 'erlaubnisscheinverfahren', 'abgrenzungsvereinbarung'],
    },
    {
        id: 'ex-bereich',
        stichworte: [
            'ex bereich', 'ex zone', 'explosionsgefaehrd', 'explosionsschutz', 'atex', 'tankstelle',
            'chemie', 'raffinerie',
        ],
        ausser: ['bauchemie'],
        dokumente: ['explosionsschutzdokument', 'erlaubnisscheinverfahren'],
    },
    {
        id: 'krankenhaus',
        stichworte: ['krankenhaus', 'klinik', 'labor', 'pflegeheim', 'pflegeeinrichtung', 'operationssaal'],
        dokumente: ['hygieneplan'],
    },
    {
        id: 'leitungen-erdverlegt',
        stichworte: [
            'erdverlegt', 'leitungen orten', 'leitungsortung', 'leitungsauskunft', 'spartenauskunft',
            'aufgrabung', 'aufgraben', 'versorgungsleitung', 'erdkabel', 'schachtschein',
        ],
        dokumente: ['leitungsauskunft', 'schutzanweisung_leitungsbetreiber'],
    },
    {
        id: 'freileitung',
        stichworte: ['freileitung', 'fahrleitung', 'oberleitung', 'hochspannungsleitung'],
        dokumente: ['freischaltung_netzbetreiber'],
    },
    {
        id: 'photovoltaik',
        stichworte: ['photovoltaik', 'pv anlage', 'pv modul', 'solaranlage', 'solarmodul'],
        dokumente: ['pv_abschaltkonzept'],
        bedingung: 'bei bestehender PV-Anlage im Arbeitsbereich',
    },
    {
        id: 'gasleitung',
        stichworte: ['gasleitung', 'gasnetz', 'gashausanschluss', 'gasanschluss', 'gasversorgung', 'erdgas', 'gasrohr'],
        dokumente: ['freigabe_gasnetzbetreiber'],
    },
    {
        id: 'kampfmittel',
        stichworte: ['kampfmittel', 'blindgaenger'],
        dokumente: ['kampfmittelauskunft', 'kampfmittelfreigabe'],
    },
    {
        id: 'nachbarbebauung',
        stichworte: [
            'nachbargebaeude', 'nachbarbebauung', 'nachbarhaus', 'angrenzende bebauung', 'angrenzende gebaeude',
            'unterfang', 'gebaeudesicherung', 'grenzbebauung',
        ],
        dokumente: ['standsicherheit_nachbarbebauung', 'beweissicherung'],
    },
    {
        id: 'durchbruch',
        stichworte: [
            'kernbohr', 'durchbruch', 'durchbrueche', 'schlitzen', 'mauerschlitz', 'wandschlitz', 'stemm',
        ],
        dokumente: ['freigabe_tragwerksplaner', 'leitungsplaene_gebaeude'],
    },
    {
        id: 'durchbruch-bestand',
        stichworte: [
            'kernbohr', 'durchbruch', 'durchbrueche', 'schlitzen', 'mauerschlitz', 'wandschlitz', 'stemm',
        ],
        dokumente: ['schadstoffgutachten'],
        bedingung: 'nur im Bestand: vor dem Eingriff Asbest, KMF und PCB im Bauteil ausschließen',
    },
    {
        id: 'baustelleneinrichtung',
        stichworte: [
            'baustelleneinrichtung', 'lagerplatz', 'lagerplaetze', 'lagerflaeche', 'baustellensicherung',
            'fahrzeugverkehr', 'baustellenverkehr', 'baustellenzufahrt',
        ],
        dokumente: ['be_plan'],
    },

    // ═══ Erd-, Tief- und Spezialtiefbau ═════════════════════════════════════
    {
        id: 'erdarbeiten',
        stichworte: ['erdarbeit', 'erdbau', 'aushub', 'baugrube', 'graben', 'ausschacht'],
        dokumente: ['baugrundgutachten'],
    },
    {
        id: 'boeschung',
        stichworte: ['boeschung'],
        dokumente: ['standsicherheit_boeschung'],
        bedingung: 'bei Wandhöhe über 5 m, Böschungswinkel über dem Normwert nach DIN 4124 oder zusätzlicher Auflast',
    },
    {
        id: 'verbau',
        stichworte: [
            'verbau', 'spundwand', 'spundung', 'traegerbohlwand', 'bohlwand', 'berliner verbau',
            'baugrubensicherung',
        ],
        // Grabenverbaugeraete nach Herstellerangabe brauchen nur die Verwendungsanleitung;
        // „verbaut“ (eingebaut) ist kein Verbau.
        ausser: ['verbaugeraet', 'verbauplatte', 'verbaubox', 'gleitschiene', 'dielenkammer', 'verbaut'],
        dokumente: ['standsicherheit_verbau'],
    },
    {
        id: 'verbaugeraet',
        stichworte: ['verbaugeraet', 'verbauplatte', 'verbaubox', 'gleitschiene', 'dielenkammer'],
        dokumente: ['verwendungsanleitung_verbaugeraet'],
    },
    {
        id: 'wasserhaltung',
        stichworte: ['wasserhaltung', 'grundwasser'],
        dokumente: ['wasserrechtliche_erlaubnis'],
    },
    {
        id: 'altlast',
        stichworte: ['altlast', 'kontamin', 'bodenverunreinig', 'belasteter boden', 'belastetem boden'],
        dokumente: [
            'erkundungsgutachten', 'arbeits_sicherheitsplan', 'anzeige_uvt_kontaminiert',
            'sachkunde_koordinator_kontaminiert',
        ],
    },
    {
        id: 'spezialtiefbau',
        stichworte: [
            'spezialtiefbau', 'bohrpfahl', 'bohrpfaehl', 'pfahlgruendung', 'schlitzwand', 'ramm',
            'verpressanker', 'erdanker', 'felsanker', 'rueckverankerung', 'duesenstrahl',
        ],
        ausser: ['gramm', 'rammschutz'],
        dokumente: ['standsicherheit_spezialtiefbaumaschine', 'baugrundgutachten'],
    },
    {
        id: 'bewehrungskorb',
        stichworte: ['bohrpfahl', 'bohrpfaehl', 'schlitzwand', 'bewehrungskorb', 'bewehrungskoerbe'],
        dokumente: ['arbeitsanweisung_bewehrungskoerbe'],
    },
    {
        id: 'erschuetterung',
        stichworte: ['ramm', 'ruettel', 'verdicht', 'vibration', 'abbruchbagger'],
        undStichworte: ['nachbar', 'angrenzend', 'bebauung', 'bestand'],
        ausser: ['gramm', 'rammschutz'],
        dokumente: ['erschuetterung', 'beweissicherung'],
    },
    {
        id: 'erschuetterung-genannt',
        stichworte: ['erschuetterung'],
        dokumente: ['erschuetterung', 'beweissicherung'],
        bedingung: 'bei Nachbarbebauung, Leitungen oder empfindlichen Anlagen im Einwirkungsbereich',
    },
    {
        // Nicht „befahren“ oder „Einstieg“: auf der Baustelle meint das meist Fahrzeuge und Gerueste.
        id: 'enge-raeume',
        stichworte: [
            'enge raeume', 'engen raeumen', 'enger raum', 'engem raum', 'umschlossene raeume',
            'umschlossenen raeumen', 'behaelterbefahrung', 'silobefahrung',
        ],
        dokumente: ['befahrerlaubnis', 'freimessprotokoll', 'rettungskonzept_enge_raeume'],
    },
    {
        id: 'kanal-schacht-behaelter',
        stichworte: ['kanal', 'kanael', 'schacht', 'schaecht', 'behaelter'],
        ausser: [
            'kabelkanal', 'lueftungskanal', 'luftkanal', 'kanalanschluss', 'schachtschein', 'ausschacht',
            'aufzugsschacht', 'lichtschacht', 'installationsschacht', 'abfallbehaelter', 'muellbehaelter',
            'druckbehaelter', 'druckgasbehaelter',
        ],
        dokumente: ['befahrerlaubnis', 'freimessprotokoll', 'rettungskonzept_enge_raeume'],
        bedingung: 'beim Einsteigen in Kanäle, Schächte, Behälter oder andere enge Räume',
    },
    {
        id: 'rohrleitungsbau',
        stichworte: ['rohrleitungsbau', 'rohrleitungsarbeit', 'rohrverleg', 'rohre verleg', 'kanalbau', 'kanalrohr'],
        dokumente: ['verlegeanleitung_rohre'],
    },

    // ═══ Rohbau und Montage ═════════════════════════════════════════════════
    {
        id: 'schalung',
        stichworte: ['schalung', 'traggeruest', 'betonierbuehne', 'deckentisch', 'kletterschalung'],
        ausser: ['dachschalung', 'holzverschalung', 'fassadenschalung'],
        dokumente: ['auv'],
    },
    {
        id: 'schalung-montage',
        stichworte: ['schalung', 'traggeruest', 'betonierbuehne', 'deckentisch', 'kletterschalung'],
        ausser: ['dachschalung', 'holzverschalung', 'fassadenschalung'],
        dokumente: ['montageanweisung'],
        bedingung: 'bei großflächig vormontierten Traggerüsten oder wenn die AuV die Montage nicht vollständig abdeckt',
    },
    {
        id: 'traggeruest',
        stichworte: ['traggeruest', 'lehrgeruest', 'deckenunterstuetzung'],
        dokumente: ['standsicherheit_traggeruest'],
        bedingung: 'wenn keine Regelausführung bzw. keine Typenstatik des Herstellers vorliegt',
    },
    {
        id: 'betonieren',
        stichworte: ['betonier', 'bodenplatte', 'ortbeton', 'ausschal'],
        ausser: ['betonierbuehne'],
        dokumente: ['ausfuehrungsstatik'],
    },
    {
        id: 'fertigteile',
        stichworte: [
            'fertigteil', 'stahlbau', 'stahlmontage', 'holzbau', 'holzmontage', 'dachstuhl', 'fassadenelement',
            // „Elementfassade“ enthaelt „fassadenelement“ nicht — die gaengigen
            // Bezeichnungen fuer vorgehaengte Fassaden deshalb ausdruecklich.
            'elementfassade', 'vorhangfassade', 'fassadenmontage',
            'glas', 'elementbau', 'montagebau',
        ],
        ausser: ['glasfaser', 'glasvlies', 'glaswolle', 'glasgewebe'],
        dokumente: ['montageanweisung', 'montage_gewicht_anschlag'],
    },
    {
        id: 'demontage',
        stichworte: ['demontage'],
        dokumente: ['montageanweisung'],
    },
    {
        // Montage der Aufzugsanlage im Schacht (Gewerk „Aufzugsbau“). Die
        // Aufzug-Staemme nur am WORTANFANG (fuehrendes Leerzeichen), sonst
        // traefen „Bauaufzugsmontage“ und „Materialaufzugsanlage“ — die haben
        // eine eigene Regel. Bewusst KEIN `ausser`: das schaltete die ganze
        // Regel ab, sobald im selben Text auch der Bauaufzug vorkommt.
        id: 'aufzugsmontage',
        stichworte: [' aufzugsmontage', ' aufzugsbau', ' aufzugsanlage', 'personenaufzug', 'fahrtreppe', 'fahrsteig'],
        dokumente: ['montageanweisung'],
    },
    {
        id: 'schutznetz',
        stichworte: ['schutznetz', 'auffangnetz', 'sicherheitsnetz'],
        dokumente: ['plan_schutznetze'],
    },
    {
        id: 'schutznetz-montage',
        stichworte: ['schutznetz', 'auffangnetz', 'sicherheitsnetz'],
        dokumente: ['montageanweisung'],
        bedingung: 'wenn die Netzmontage besondere sicherheitstechnische Angaben erfordert',
    },

    // ═══ Gerueste und Absturz ═══════════════════════════════════════════════
    {
        id: 'geruest',
        stichworte: ['geruest', 'schutzdach', 'schutzdaech'],
        // Traggerueste tragen Schalung, keine Personen — eigene Regeln oben.
        ausser: ['traggeruest', 'lehrgeruest'],
        dokumente: ['auv', 'pruefprotokoll_geruest'],
    },
    {
        id: 'sondergeruest',
        stichworte: [
            'sondergeruest', 'haengegeruest', 'konsolgeruest', 'auslegergeruest', 'traegergeruest',
            'geruestueberbrueckung', 'geruest ueberbrueckung',
        ],
        dokumente: ['standsicherheit_geruest'],
    },
    {
        id: 'geruest-bekleidung',
        stichworte: ['geruest'],
        undStichworte: ['plane', 'netz', 'bekleid', 'verkleid', 'einhaus'],
        ausser: ['geplant', 'planer', 'traggeruest'],
        dokumente: ['standsicherheit_geruest'],
        bedingung: 'falls die AuV die Bekleidung (Planen, Netze) nicht abdeckt',
    },
    {
        id: 'rollgeruest',
        stichworte: ['fahrbare arbeitsbuehne', 'fahrbaren arbeitsbuehne', 'rollgeruest', 'fahrgeruest'],
        dokumente: ['auv'],
    },
    {
        id: 'seitenschutz',
        stichworte: ['seitenschutzsystem', 'randsicherung', 'dachschutzwand'],
        dokumente: ['auv', 'pruefung_seitenschutz'],
    },
    {
        id: 'dachschutzwand',
        stichworte: ['dachschutzwand'],
        dokumente: ['brauchbarkeit_dachschutzwand'],
    },
    {
        id: 'psaga',
        stichworte: ['psaga', 'psa gegen absturz', 'anseilschutz', 'seilzugang', 'seilunterstuetzt', 'industriekletter'],
        dokumente: ['rettungskonzept_psaga'],
    },
    {
        id: 'dach',
        stichworte: ['dach', 'photovoltaik', 'lichtkuppel', 'lichtband'],
        ausser: ['schutzdach', 'dachfanggeruest', 'unter dach', 'dachgeschoss', 'dachboden', 'freiflaech'],
        dokumente: ['durchtrittsicherheit', 'pruefnachweis_anschlageinrichtung'],
    },
    {
        id: 'haengebuehne',
        stichworte: ['haengende arbeitsbuehne', 'haengebuehne', 'arbeitssitz'],
        dokumente: ['anzeige_haengende_arbeitsbuehne'],
    },

    // ═══ Hebezeuge ══════════════════════════════════════════════════════════
    {
        id: 'turmdrehkran',
        stichworte: ['turmdrehkran', 'baukran', 'obendreher', 'untendreher', 'schnellmontagekran'],
        dokumente: ['standsicherheit_kran', 'kranpruefbuch', 'montageanweisung'],
    },
    {
        id: 'turmdrehkran-fuehrerhaus',
        stichworte: ['turmdrehkran', 'baukran', 'obendreher'],
        dokumente: ['rettungskonzept_kranfuehrer'],
        bedingung: 'bei Kranen mit Führerhaus',
    },
    {
        id: 'mehrere-krane',
        stichworte: ['kran'],
        undStichworte: ['mehrere', 'weitere', 'zweit', 'zwei', 'ueberschneid', 'nachbarbaustelle'],
        ausser: KRAN_AUSSER,
        dokumente: ['kranueberschneidung'],
    },
    {
        id: 'mobilkran',
        stichworte: ['mobilkran', 'autokran', 'fahrzeugkran', 'raupenkran', 'teleskopkran', 'ladekran'],
        dokumente: ['abstuetznachweis', 'kranpruefbuch'],
    },
    {
        id: 'personenaufnahme',
        stichworte: ['personenaufnahme', 'arbeitskorb', 'personenkorb', 'mannkorb'],
        ausser: ['hubarbeitsbuehne', 'hubsteiger', 'teleskopbuehne'],
        dokumente: ['statik_personenaufnahmemittel', 'anzeige_personenaufnahmemittel'],
    },
    {
        id: 'bauaufzug',
        stichworte: ['bauaufzug', 'transportbuehne', 'materialaufzug'],
        dokumente: ['statik_bauaufzug'],
        bedingung: 'nur bei Verankerung abweichend von der Betriebsanleitung',
    },
    {
        id: 'silo',
        stichworte: ['silo'],
        ausser: ['siloxan', 'silobefahrung'],
        dokumente: ['nachweis_silo'],
    },
    {
        id: 'kran-luftfahrt',
        stichworte: ['kran'],
        undStichworte: ['flughafen', 'flugplatz', 'landeplatz', 'luftfahrt', 'einflugschneise', 'anflug'],
        ausser: KRAN_AUSSER,
        dokumente: ['luftrechtliche_genehmigung'],
    },

    // ═══ Abbruch, Schadstoffe, Staeube ══════════════════════════════════════
    {
        id: 'abbruch',
        stichworte: ['abbruch', 'rueckbau', 'entkern', 'abriss'],
        ausser: ['abbruchkante'],
        dokumente: ['schadstoffgutachten', 'abbruchanweisung'],
    },
    {
        id: 'abbruch-standsicherheit',
        stichworte: ['abbruch', 'rueckbau', 'abriss'],
        ausser: ['abbruchkante'],
        dokumente: ['standsicherheit_abbruch'],
        bedingung: 'wenn tragende Bauteile abgebrochen oder geschwächt werden',
    },
    {
        id: 'asbest',
        stichworte: ['asbest'],
        dokumente: ['sachkunde_asbest', 'anzeige_asbest', 'arbeitsplan_trgs519', 'betriebsanweisung_gefahrstoffe'],
    },
    {
        id: 'kmf-alt',
        stichworte: KMF,
        undStichworte: KMF_ALT,
        dokumente: ['arbeitsplan_trgs521', 'schadstoffgutachten'],
    },
    {
        id: 'kmf-neu',
        stichworte: KMF,
        ausser: KMF_ALT,
        dokumente: ['sdb'],
    },
    {
        id: 'pcb-pak',
        stichworte: ['pcb', 'pak', 'teer', 'holzschutzmittel', 'pentachlorphenol', 'pcp', 'lindan'],
        ausser: ['paket'],
        dokumente: ['arbeits_sicherheitsplan'],
    },
    {
        // Nicht nacktes „aufbruch“: ein Bodenplattenaufbruch im Gebaeude ist kein Strassenaufbruch.
        id: 'strassenaufbruch',
        stichworte: ['strassenaufbruch', 'fahrbahnaufbruch', 'gehwegaufbruch', 'aufbruch der strasse', 'aufbruch der fahrbahn'],
        dokumente: ['pak_untersuchung'],
    },
    {
        id: 'asphalt-ausbau',
        stichworte: ['asphalt'],
        undStichworte: ['fraes', 'abbruch', 'rueckbau', 'ausbau', 'aufnehmen', 'abtrag'],
        dokumente: ['pak_untersuchung'],
    },
    {
        id: 'quarzstaub',
        stichworte: [
            'stemm', 'fraes', 'schleif', 'quarz', 'schlagbohr', 'schlitzfraes', 'mauernutfraes', 'sandstrahl',
            'betonbearbeitung',
        ],
        ausser: ['holz', 'parkett', 'spachtel', 'metall'],
        dokumente: ['gb_trgs559'],
    },
    {
        id: 'holzstaub',
        stichworte: ['holzstaub', 'holzbearbeitung', 'parkettschleif', 'parkett schleif', 'holzschleif', 'holz schleif'],
        dokumente: ['gb_trgs553'],
    },
    {
        id: 'loesemittel',
        stichworte: [
            'loesemittel', 'lack', 'kleb', 'epoxid', 'bitumen', 'beschichtung', 'kunstharz', 'gefahrstoff',
        ],
        // Asbest und Altlasten laufen ueber den Arbeitsplan, nicht ueber Datenblaetter;
        // „Schlacke“ und „Aufkleber“ sind keine Lacke bzw. Kleber.
        ausser: ['asbest', 'kontamin', 'altlast', 'schlack', 'aufkleber'],
        dokumente: ['sdb', 'betriebsanweisung_gefahrstoffe'],
    },
    {
        id: 'biostoffe',
        stichworte: ['biostoff', 'biologische arbeitsstoff', 'schimmel', 'taubenkot', 'vogelkot', 'faekal'],
        dokumente: ['gutachten_biostoffe', 'betriebsanweisung_biostoffe'],
    },
    {
        id: 'diesel-innen',
        stichworte: ['diesel', 'verbrennungsmotor', 'abgas'],
        undStichworte: LAGE_INNENRAUM,
        ausser: ['schall'],
        dokumente: ['nachweis_trgs554', 'lueftungskonzept'],
    },

    // ═══ Brand, Energie, Sonderverfahren ════════════════════════════════════
    {
        id: 'heissarbeiten',
        stichworte: [
            'schweiss', 'brennschneid', 'trennschleif', 'trenn schleif', 'loeten', 'loetarbeit', 'heissarbeit',
            'offene flamme', 'offener flamme', 'flammarbeit', 'schneidbrenner', 'autogen',
        ],
        dokumente: ['schweisserlaubnis'],
    },
    {
        id: 'heissarbeiten-bma',
        stichworte: [
            'schweiss', 'brennschneid', 'trennschleif', 'trenn schleif', 'loeten', 'loetarbeit', 'heissarbeit',
            'offene flamme', 'offener flamme', 'flammarbeit', 'schneidbrenner', 'autogen',
        ],
        dokumente: ['bma_abschaltung'],
        bedingung: 'nur im Bestand mit Brandmeldeanlage',
    },
    {
        id: 'fluessiggas',
        stichworte: ['fluessiggas', 'propan', 'gasbrenner', 'gasflasche', 'gasheiz'],
        dokumente: ['betriebsanweisung_fluessiggas'],
    },
    {
        id: 'heizgeraet',
        stichworte: ['heizgeraet', 'heizkanone', 'bauheizung', 'schmelzgeraet', 'bitumenkocher'],
        dokumente: ['betriebsanweisung_fluessiggas'],
        bedingung: 'bei Betrieb mit Flüssiggas',
    },
    {
        id: 'brandschutz',
        stichworte: ['brandschutz'],
        ausser: [
            'brandschutzhelfer', 'brandschutztechnik', 'brandschutzklappe', 'brandschutzbeschichtung',
            'brandschutzanstrich', 'abschottung', 'brandschutzverglasung', 'brandschutztuer', 'brandschutztor',
            'brandschutzplatte', 'brandschutzmanschette', 'brandschutzbekleidung', 'sprinkler',
        ],
        dokumente: ['brandschutzordnung'],
    },
    {
        id: 'brandschutz-bestand',
        stichworte: ['brandschutz'],
        ausser: [
            'brandschutzhelfer', 'brandschutztechnik', 'brandschutzklappe', 'brandschutzbeschichtung',
            'brandschutzanstrich', 'abschottung', 'brandschutzverglasung', 'brandschutztuer', 'brandschutztor',
            'brandschutzplatte', 'brandschutzmanschette', 'brandschutzbekleidung', 'sprinkler',
        ],
        dokumente: ['brandschutzkonzept'],
        bedingung: 'im Bestand oder bei laufendem Betrieb',
    },
    {
        id: 'rettungswege',
        stichworte: ['rettungsweg', 'fluchtweg', 'flucht und rettung', 'notausgang'],
        dokumente: ['flucht_rettungsplan', 'alarmplan'],
    },
    {
        id: 'elektro-anlagen',
        stichworte: [
            'elektrische anlage', 'elektrischen anlage', 'elektroanlage', 'freischalt', 'spannungsfrei',
            'schaltanlage', 'trafostation', 'mittelspannung',
        ],
        dokumente: ['freischaltprotokoll'],
    },
    {
        id: 'elektro-betreiber',
        stichworte: [
            'elektrische anlage', 'elektrischen anlage', 'elektroanlage', 'freischalt', 'spannungsfrei',
            'schaltanlage', 'trafostation', 'mittelspannung',
        ],
        dokumente: ['schaltberechtigung'],
        bedingung: 'bei bestehenden Anlagen eines Betreibers',
    },
    {
        id: 'unter-spannung',
        stichworte: ['unter spannung'],
        dokumente: ['arbeitsanweisung_aus'],
    },
    {
        id: 'baustrom',
        stichworte: ['baustrom', 'stromversorgung', 'stromanschluss', 'versorgung'],
        ausser: [
            'erste hilfe', 'ersthelfer', 'notfall', 'medizinisch', 'versorgungsleitung', 'versorgungsschacht',
            'versorgungsnetz', 'versorgungstrasse', 'versorgungsunternehmen', 'wasserversorgung', 'loeschwasser',
            'gasversorgung', 'waermeversorgung', 'fernwaerme',
        ],
        dokumente: ['pruefprotokoll_baustromverteiler'],
    },
    {
        id: 'stromerzeuger',
        stichworte: ['stromerzeuger', 'stromaggregat', 'aggregat', 'generator'],
        ausser: ['hydraulikaggregat', 'kaelteaggregat', 'klimaaggregat', 'lueftungsaggregat', 'pumpenaggregat'],
        dokumente: ['betriebsanweisung_stromerzeuger'],
    },
    {
        id: 'tankanlage',
        stichworte: ['tankanlage', 'dieseltank', 'kraftstofftank', 'betankung', 'kraftstofflager'],
        ausser: ['tankstelle'],
        dokumente: ['pruefnachweis_tankanlage', 'sdb'],
    },
    {
        id: 'sprengen',
        stichworte: ['spreng'],
        ausser: ['rasensprenger', 'sprengring'],
        dokumente: ['sprengerlaubnis', 'sprengplan'],
    },
    {
        id: 'druckluft',
        stichworte: ['senkkasten', 'caisson', 'in druckluft', 'unter druckluft', 'druckluftarbeit', 'druckluftbereich'],
        dokumente: ['anzeige_druckluft'],
    },
    {
        id: 'druckluft-kombi',
        stichworte: ['druckluft'],
        undStichworte: ['tunnel', 'vortrieb', 'senkkasten', 'caisson', 'schleuse'],
        dokumente: ['anzeige_druckluft'],
    },
    {
        id: 'laser',
        stichworte: ['laser'],
        dokumente: ['laserschutzbeauftragter', 'betriebsanweisung_laser'],
        bedingung: 'nur bei Lasern der Klassen 3R, 3B und 4',
    },
    {
        id: 'strahlung',
        stichworte: [
            'durchstrahlung', 'isotop', 'radiograf', 'radiograph', 'roentgen', 'strahlenquelle', 'radioaktiv',
            'ionisierende strahlung', 'strahlenschutz',
        ],
        dokumente: ['strahlenschutz'],
    },

    // ═══ Verkehr, Wasser, Bahn ══════════════════════════════════════════════
    {
        id: 'verkehrsraum',
        stichworte: [
            'verkehrsraum', 'strassenbaustelle', 'strassenbau', 'verkehrssicherung', 'verkehrsfuehrung',
            'arbeitsstelle an strassen', 'gehweg', 'fahrbahn',
        ],
        ausser: ['kranfahrbahn', 'kranbahn'],
        dokumente: ['vrao', 'sondernutzungserlaubnis'],
    },
    {
        id: 'gleis',
        stichworte: [
            'gleis', 'bahnanlage', 'eisenbahn', 'bahnstrecke', 'bahnkoerper', 'bahnbetrieb', 'bahngelaende',
            'bahnsteig', 'strassenbahn', 'stadtbahn',
        ],
        ausser: ['krangleis', 'kranschiene'],
        dokumente: ['betra', 'sicherungsplan_gleis'],
    },
    {
        // Nicht „am/über Wasser“: das trifft auch „am Wasserhahn“ und „über Wasserleitungen“.
        id: 'gewaesser',
        stichworte: [
            'gewaesser', 'ufer', 'vom wasser aus', 'wasserseitig', 'wasserbau', 'wasserstrasse', 'hafen',
            'kaimauer', 'hochwasser',
        ],
        ausser: ['aeufer', 'rufer', 'flughafen'],
        dokumente: ['rettungskonzept_wasser', 'hochwasser_raeumungsplan'],
    },
    {
        id: 'gewaesser-wsa',
        stichworte: [
            'gewaesser', 'ufer', 'vom wasser aus', 'wasserseitig', 'wasserbau', 'wasserstrasse', 'hafen',
            'kaimauer',
        ],
        ausser: ['aeufer', 'rufer', 'flughafen'],
        dokumente: ['genehmigung_wsa'],
        bedingung: 'an Bundeswasserstraßen und in Häfen',
    },
    {
        id: 'taucher',
        stichworte: ['taucher', 'tauchar', 'tauchgang'],
        dokumente: ['tauchplan'],
        bedingung: 'bei Tauchtiefen über 10 m oder besonderen Erschwernissen',
    },
    {
        id: 'schwimmend',
        stichworte: LAGE_SCHWIMMEND,
        dokumente: ['stabilitaet_schwimmend', 'schiffsattest', 'rettungskonzept_wasser'],
    },

    // ═══ Kombiregeln Geraet × Lage ══════════════════════════════════════════
    // „Bagger“ allein loest hier nichts aus — erst die Lage macht den Nachweis noetig.
    {
        id: 'geraet-schwimmend',
        stichworte: GERAETE,
        undStichworte: LAGE_SCHWIMMEND,
        ausser: GERAETE_AUSSER,
        dokumente: ['stabilitaet_schwimmend', 'standsicherheit_geraet_schwimmend', 'rettungskonzept_wasser'],
    },
    {
        id: 'geraet-decke',
        stichworte: GERAETE,
        undStichworte: LAGE_DECKE,
        ausser: [...GERAETE_AUSSER, ...LAGE_DECKE_AUSSER],
        dokumente: ['statik_belastete_konstruktion'],
    },
    {
        id: 'geraet-baugrube',
        stichworte: GERAETE,
        undStichworte: LAGE_BAUGRUBE,
        // Der Aushub selbst ist der Normalfall, keine Auflast am Rand.
        ausser: [...GERAETE_AUSSER, 'aushub', 'ausheb', 'verbaut'],
        dokumente: ['standsicherheit_boeschung', 'standsicherheit_verbau'],
        bedingung: 'ohne Nachweis nur mit Mindestabstand zur Böschungskante nach DIN 4124 (1 m bis 12 t, 2 m bis 40 t Gesamtgewicht)',
    },
    {
        id: 'geraet-untergrund',
        stichworte: GERAETE,
        undStichworte: LAGE_UNTERGRUND,
        ausser: GERAETE_AUSSER,
        dokumente: ['baugrundgutachten', 'abstuetznachweis'],
    },
    {
        id: 'geraet-verkehrsraum',
        stichworte: GERAETE,
        undStichworte: LAGE_VERKEHRSRAUM,
        ausser: [...GERAETE_AUSSER, 'baustrasse', 'baustellenstrasse', 'kranfahrbahn'],
        dokumente: ['vrao', 'sondernutzungserlaubnis'],
    },
    {
        id: 'geraet-innenraum',
        stichworte: GERAETE,
        undStichworte: LAGE_INNENRAUM,
        ausser: [...GERAETE_AUSSER, 'schall'],
        dokumente: ['nachweis_trgs554', 'lueftungskonzept'],
        bedingung: 'bei Geräten mit Verbrennungsmotor',
    },
    {
        id: 'bagger-heben',
        stichworte: ['bagger', 'lader'],
        undStichworte: [
            'hebezeug', 'heben', 'hebearbeit', 'hebeeinsatz', 'rohrverleg', 'rohre verleg', 'lastentransport',
            'lasttransport', 'anschlagen', 'anschlagmittel',
        ],
        ausser: ['ausheb'],
        dokumente: ['betriebsanleitung_hebezeugeinsatz'],
    },
];
